// LeadDetailLeft — left column of the Ringy-style Lead Detail.
//   LEAD DETAILS sub-tab: identity, contact (click-to-copy phone/email),
//     location + auto-refreshing local time, birthday, CLICK TO CALL (with
//     caller-ID picker), DISPOSITION dropdown, inline-editable Other Info,
//     meta rows, and bottom actions.
//   SALE INFO sub-tab: inline-editable sale fields.
//
// Inline edits persist via PUT /leads/:id (tenant-scoped server-side).

import React, { useState, useEffect, useRef } from 'react';
import { Pencil, Mail, Copy, MapPin, Clock, DollarSign, CalendarPlus, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiPut } from '../../lib/api.js';
import { fmtMoney, fmtDate, fmtFromNow } from '../../lib/format.js';
import ClickToCall from '../ClickToCall.jsx';
import CallerIdDropdown from './CallerIdDropdown.jsx';
import DispositionDropdown from './DispositionDropdown.jsx';
import ActiveDripBadge from './ActiveDripBadge.jsx';
import BirthdayRow from './BirthdayRow.jsx';
import EditLeadModal from './EditLeadModal.jsx';
import LeadCommActions from './LeadCommActions.jsx';
import PhoneLineRow from './PhoneLineRow.jsx';
import VendorRow from './VendorRow.jsx';
import { resolveLeadCost } from '../../lib/leadCost.js';

function copy(text, label) {
  if (!text) return;
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} copied`),
    () => toast.error('Copy failed'),
  );
}

// A compact inline-editable field row.
function EditableRow({ label, value, field, leadId, type = 'text', options, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');

  useEffect(() => { setVal(value ?? ''); }, [value]);

  async function save() {
    setEditing(false);
    if ((val ?? '') === (value ?? '')) return;
    try {
      await apiPut(`/leads/${leadId}`, { [field]: val === '' ? null : val });
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
      setVal(value ?? '');
    }
  }

  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <span className="text-[11px] text-white/45 shrink-0">{label}</span>
      {editing ? (
        options ? (
          <select
            autoFocus
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={save}
            className="text-[11px] bg-civix-bg border border-civix-purple/50 rounded-sm px-1.5 py-0.5 text-white/85 outline-hidden"
          >
            <option value="">—</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <input
            autoFocus
            type={type}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => e.key === 'Enter' && save()}
            className="text-[11px] bg-civix-bg border border-civix-purple/50 rounded-sm px-1.5 py-0.5 text-white/85 outline-hidden w-32 text-right"
          />
        )
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="text-[11px] text-white/80 hover:text-white flex items-center gap-1 group"
        >
          <span className="truncate max-w-[140px]">{value || '—'}</span>
          <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60" />
        </button>
      )}
    </div>
  );
}

// Inline-editable name header (First + Last). Mirrors EditableRow's save/toast/
// revert pattern but persists both name fields in one PUT. A lead must keep a
// name, so a save with BOTH fields empty reverts instead of persisting.
function EditableName({ firstName, lastName, leadId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [first, setFirst] = useState(firstName ?? '');
  const [last, setLast] = useState(lastName ?? '');
  // Guards a double-save when Enter (which exits edit mode) is followed by the
  // group blur that unmounting the inputs can fire.
  const savedRef = useRef(false);

  useEffect(() => {
    setFirst(firstName ?? '');
    setLast(lastName ?? '');
  }, [firstName, lastName]);

  const display = [firstName, lastName].filter(Boolean).join(' ') || 'Unnamed lead';

  function revert() {
    setFirst(firstName ?? '');
    setLast(lastName ?? '');
    setEditing(false);
  }

  function beginEdit() {
    savedRef.current = false;
    setFirst(firstName ?? '');
    setLast(lastName ?? '');
    setEditing(true);
  }

  function cancel() {
    savedRef.current = true; // suppress the trailing group-blur save
    revert();
  }

  async function save() {
    if (savedRef.current) return;
    savedRef.current = true;
    const f = first.trim();
    const l = last.trim();
    setEditing(false);
    // A lead must have a name — never persist both fields empty.
    if (!f && !l) { revert(); return; }
    if (f === (firstName ?? '') && l === (lastName ?? '')) return;
    try {
      await apiPut(`/leads/${leadId}`, { firstName: f, lastName: l });
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
      revert();
    }
  }

  if (editing) {
    return (
      <div
        className="flex items-center gap-1.5"
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) save(); }}
      >
        <input
          autoFocus
          aria-label="First name"
          value={first}
          onChange={(e) => setFirst(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); else if (e.key === 'Escape') cancel(); }}
          className="text-sm font-bold bg-civix-bg border border-civix-purple/50 rounded-sm px-1.5 py-0.5 text-white outline-hidden w-24"
        />
        <input
          aria-label="Last name"
          value={last}
          onChange={(e) => setLast(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); else if (e.key === 'Escape') cancel(); }}
          className="text-sm font-bold bg-civix-bg border border-civix-purple/50 rounded-sm px-1.5 py-0.5 text-white outline-hidden w-24"
        />
      </div>
    );
  }

  return (
    <button
      onClick={beginEdit}
      aria-label="Edit name"
      className="flex items-center gap-1.5 group max-w-full"
    >
      <h2 className="text-sm font-bold text-white truncate">{display}</h2>
      <Pencil className="w-3.5 h-3.5 text-white/40 group-hover:text-white/70" />
    </button>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[11px] text-white/40">{label}</span>
      <span className="text-[11px] text-white/70 truncate max-w-[150px]">{value || '—'}</span>
    </div>
  );
}

function useLocalTime(timezone) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone || undefined, hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    }).format(now);
  } catch {
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(now);
  }
}

export default function LeadDetailLeft({ lead, leadId, onChange, onScheduleFollowUp, onCreateAppointment, onMarkSold }) {
  const [subTab, setSubTab] = useState('details');
  const [callerId, setCallerId] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const localTime = useLocalTime(lead?.timezone);

  if (!lead) return <div className="text-xs text-white/40 p-3">Loading…</div>;

  return (
    <div className="flex flex-col h-full">
      {/* Sub-tabs */}
      <div className="flex gap-1 mb-3">
        {[['details', 'LEAD DETAILS'], ['sale', 'SALE INFO']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setSubTab(id)}
            aria-pressed={subTab === id}
            className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold transition
              ${subTab === id
                ? 'bg-civix-purple/25 text-white border border-civix-purple/50'
                : 'text-white/50 hover:text-white/80 border border-transparent'}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto pr-1 space-y-3">
        {subTab === 'details' ? (
          <>
            {/* Identity + quick actions */}
            <div>
              <div className="flex items-start justify-between gap-2">
                <EditableName
                  firstName={lead.firstName}
                  lastName={lead.lastName}
                  leadId={leadId}
                  onSaved={onChange}
                />
                <button type="button" onClick={() => setEditOpen(true)} aria-label="Edit lead details" title="Edit lead details" data-testid="edit-lead-btn" className="shrink-0 rounded-md p-1.5 text-civix-purpleSoft hover:bg-white/5 hover:text-white">
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-2"><LeadCommActions lead={lead} leadId={leadId} onChange={onChange} /></div>
            </div>

            {/* Contact */}
            <div className="space-y-1">
              <PhoneLineRow lead={lead} leadId={leadId} onChange={onChange} />
              {lead.email && (
                <button
                  onClick={() => copy(lead.email, 'Email')}
                  className="flex items-center gap-1.5 text-xs text-white/80 hover:text-white group"
                >
                  <Mail className="w-3.5 h-3.5 text-white/40" />
                  <span className="truncate max-w-[180px]">{lead.email}</span>
                  <Copy className="w-3 h-3 opacity-0 group-hover:opacity-60" />
                </button>
              )}
              <div className="flex items-center gap-1.5 text-xs text-white/70">
                <MapPin className="w-3.5 h-3.5 text-white/40" />
                {[lead.address, lead.city, [lead.state, lead.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '—'}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-white/70">
                <Clock className="w-3.5 h-3.5 text-white/40" />
                Local time: {localTime}
              </div>
              <BirthdayRow lead={lead} leadId={leadId} onChange={onChange} />
            </div>

            {/* Call + disposition */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <ClickToCall phone={lead.phone} leadId={leadId} callCount={lead.callCount || 0} className="flex-1 justify-center py-2" />
                <CallerIdDropdown value={callerId} onChange={setCallerId} />
              </div>
              <DispositionDropdown leadId={leadId} current={lead.disposition} onChange={onChange} />
              <ActiveDripBadge leadId={leadId} />
            </div>

            {/* Other Info — inline editable */}
            <div className="rounded-lg bg-civix-panel/50 border border-civix-border p-2.5">
              <h4 className="text-[10px] uppercase tracking-wide text-white/40 mb-1">Other Info</h4>
              <EditableRow label="Age range" value={lead.ageRange} field="ageRange" leadId={leadId} onSaved={onChange} />
              <EditableRow label="Existing insurance" value={lead.existingLifeInsurance} field="existingLifeInsurance" leadId={leadId} onSaved={onChange} />
              <EditableRow label="Beneficiary" value={lead.beneficiary} field="beneficiary" leadId={leadId} onSaved={onChange} />
              <EditableRow label="Smoker" value={lead.smoker} field="smoker" leadId={leadId} options={['yes', 'no']} onSaved={onChange} />
              <EditableRow label="Family" value={lead.family} field="family" leadId={leadId} onSaved={onChange} />
              <EditableRow label="Tier" value={lead.tier} field="tier" leadId={leadId} options={['Tier 1', 'Tier 2', 'Tier 3', 'Broad']} onSaved={onChange} />
            </div>

            {/* Meta */}
            <div className="rounded-lg bg-civix-panel/50 border border-civix-border p-2.5">
              <VendorRow lead={lead} leadId={leadId} onChange={onChange} />
              <MetaRow label="Created" value={fmtDate(lead.createdAt)} />
              <MetaRow label="Last Texted" value={lead.lastTextedAt ? fmtFromNow(lead.lastTextedAt) : '—'} />
              <MetaRow label="Last Called" value={lead.lastCalledAt ? fmtFromNow(lead.lastCalledAt) : '—'} />
            </div>

            {/* Bottom actions */}
            <div className="grid grid-cols-1 gap-1.5 pt-1">
              <button onClick={onScheduleFollowUp} className="btn-ghost text-[11px] py-2 flex items-center justify-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> SCHEDULE FOLLOW UP
              </button>
              <button onClick={onCreateAppointment} className="btn-ghost text-[11px] py-2 flex items-center justify-center gap-1.5">
                <CalendarPlus className="w-3.5 h-3.5" /> CREATE APPOINTMENT
              </button>
              <button onClick={onMarkSold} className="btn-primary text-[11px] py-2 flex items-center justify-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> MARK AS SOLD
              </button>
            </div>
          </>
        ) : (
          /* SALE INFO */
          <div className="rounded-lg bg-civix-panel/50 border border-civix-border p-2.5">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-white/40 mb-1">
              <DollarSign className="w-3.5 h-3.5" /> Sale Info
            </div>
            <EditableRow label="Sale amount" value={lead.saleAmount} field="saleAmount" leadId={leadId} type="number" onSaved={onChange} />
            <EditableRow label="Commission" value={lead.commission} field="commission" leadId={leadId} type="number" onSaved={onChange} />
            <EditableRow label="Cost for lead" value={resolveLeadCost(lead) ?? lead.costForLead} field="costForLead" leadId={leadId} type="number" onSaved={onChange} />
            <EditableRow label="Sold date" value={lead.soldDate ? String(lead.soldDate).slice(0, 10) : ''} field="soldDate" leadId={leadId} type="date" onSaved={onChange} />
            <EditableRow label="Product sold" value={lead.productSold} field="productSold" leadId={leadId} onSaved={onChange} />
            <EditableRow label="Face amount" value={lead.faceAmount} field="faceAmount" leadId={leadId} type="number" onSaved={onChange} />
            <EditableRow label="Carrier" value={lead.carrier} field="carrier" leadId={leadId} onSaved={onChange} />
            <div className="mt-2 pt-2 border-t border-civix-border">
              <MetaRow label="Sale total" value={fmtMoney(lead.saleAmount)} />
            </div>
          </div>
        )}
      </div>
      {editOpen && <EditLeadModal lead={lead} leadId={leadId} onClose={() => setEditOpen(false)} onSaved={onChange} />}
    </div>
  );
}
